import React, { useEffect, useRef, useState } from 'react';
import { widget, IChartingLibraryWidget, Timezone, LanguageCode } from '../../charting_library/charting_library.min';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, selectCurrentColorTheme } from '../../modules';
import { getTradingChartTimezone } from './timezones';
import { widgetOptions } from './config';
import { DEFAULT_GROUP } from '../../stock_config/stock-ui-config';
import { fetchTickersGroupRequest } from '../../modules/public/tickers/actions';
import { appUrls } from '../../api-new/urls';
import { useLocation } from 'react-router';

interface StockChartProps {
    data: Array<{
        time: string;
        open: number;
        high: number;
        low: number;
        close: number;
        volume: number;
    }>
}

const StockChart: React.FC<StockChartProps> = ({ data}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const tvWidgetRef = useRef<IChartingLibraryWidget | null>(null);
    const colorTheme = useSelector((state: RootState) => selectCurrentColorTheme(state));
    const locale: LanguageCode = 'en';
    const timezone: Timezone = getTradingChartTimezone();
    const currentGroup = "HOSE";
    const [newItem, setNewItem] = useState(null);
    const [realTimeData, setRealTimeData] = useState(null);
    const [datafeed, setDatafeed] = useState<any>(null);
    const datafeedRef = useRef<any>(null);
    const subscriptionRef = useRef<{ symbol: string; resolution: string; subscriberUID: string } | null>(null);
    const lastSubscriberUIDRef = useRef<string | null>(null);
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const [currentTickerString, setCurrentTickerString] = useState<string>(params.get('order-ticker') || 'FPT');
    console.log('currentTickerString', currentTickerString);
      // Effects
      useEffect(() => {
        const params = new URLSearchParams(search);
        const ticker = params.get('order-ticker') || 'FPT';
        setCurrentTickerString(ticker);
    }, [search]);

    useEffect(() => {
        if (!chartContainerRef.current || !currentTickerString) return;

        const newDatafeed = {
            onReady: (callback: (data: any) => void) => {
                setTimeout(() => callback({
                    supported_resolutions: ['1D', '1W', '1M'],
                    exchanges: [
                        {
                            value: 'HOSE',
                            name: 'HOSE',
                            desc: 'HOSE Exchange'
                        }
                    ],
                    symbols_types: [
                        {
                            name: 'stock',
                            value: 'stock'
                        }
                    ]
                }), 0);
            },
            resolveSymbol: (
                symbolName: string,
                onResolve: (symbolInfo: any) => void,
                onError: (error: any) => void,
            ) => {
                setTimeout(() => {
                    onResolve({
                        ticker: symbolName,
                        name: symbolName,
                        description: `${symbolName} Stock`,
                        type: 'stock',
                        session: '0900-1600',
                        timezone: timezone,
                        minmov: 1,
                        pricescale: 100,
                        has_intraday: true,
                        supported_resolutions: ['1W', '1D', '1M'],
                        volume_precision: 2,
                        data_status: 'streaming',
                        exchange: 'HOSE',
                        currency_code: 'VND',
                    });
                }, 0);
            },
            getBars: (
                symbolInfo: any,
                resolution: string,
                from: number,
                to: number,
                onHistoryCallback: (bars: any[], meta: { noData: boolean }) => void,
                onErrorCallback: (error: any) => void,
            ) => {
                try {
                    const fromMs = from * 1000;
                    const toMs = to * 1000;
                    const earliestDataTime = new Date(data[0]?.time).getTime();
                    const latestDataTime = new Date(data[data.length - 1]?.time).getTime();

                    if (toMs < earliestDataTime || fromMs > latestDataTime) {
                        onHistoryCallback([], { noData: true });
                        return;
                    }
                    const bars = data
                        .filter((item) => {
                            const itemTime = new Date(item.time).getTime();
                            return itemTime >= fromMs && itemTime <= toMs;
                        })
                        .map((item) => ({
                            time: new Date(item.time).getTime(),
                            open: item.open,
                            high: item.high,
                            low: item.low,
                            close: item.close,
                            volume: item.volume,
                        }));
                    onHistoryCallback(bars, { noData: bars.length === 0 });
                } catch (error) {
                    console.error('Error in getBars:', error);
                    onErrorCallback(error);
                }
            },
            subscribeBars: (
                symbolInfo: any,
                resolution: string,
                onRealtimeCallback: (bar: any) => void,
                subscriberUID: string,
                onResetCacheNeededCallback: () => void,
            ) => {
                console.log('Subscribing to bars:', {
                    symbol: symbolInfo.ticker,
                    resolution,
                    subscriberUID,
                    hasCallback: !!onRealtimeCallback,
                    lastSubscriberUID: lastSubscriberUIDRef.current
                });

                // Store callbacks in the datafeed object
                newDatafeed.onRealtimeCallback = onRealtimeCallback;
                newDatafeed.onResetCacheNeededCallback = onResetCacheNeededCallback;
                
                // Store current subscription info
                const subscription = {
                    symbol: symbolInfo.ticker,
                    resolution,
                    subscriberUID
                };
                newDatafeed.currentSubscription = subscription;
                subscriptionRef.current = subscription;
                lastSubscriberUIDRef.current = subscriberUID;

                // Update both state and ref
                setDatafeed({...newDatafeed});
                datafeedRef.current = {...newDatafeed};
            },
            unsubscribeBars: (subscriberUID: string) => {
                console.log('Unsubscribing from bars:', {
                    subscriberUID,
                    lastSubscriberUID: lastSubscriberUIDRef.current,
                    currentSubscription: subscriptionRef.current
                });

                // Only clear if this is our current subscription and it's different from the last one
                if (subscriptionRef.current?.subscriberUID === subscriberUID && 
                    subscriberUID !== lastSubscriberUIDRef.current) {
                    console.log('Clearing callbacks for subscription:', subscriberUID);
                    
                    // Clear callbacks and subscription info
                    newDatafeed.onRealtimeCallback = null;
                    newDatafeed.onResetCacheNeededCallback = null;
                    newDatafeed.currentSubscription = null;
                    subscriptionRef.current = null;

                    // Update both state and ref
                    setDatafeed({...newDatafeed});
                    datafeedRef.current = {...newDatafeed};
                } else {
                    console.log('Skipping unsubscribe for:', subscriberUID);
                }
            },
            onRealtimeCallback: null as ((bar: any) => void) | null,
            onResetCacheNeededCallback: null as (() => void) | null,
            currentSubscription: null as { symbol: string; resolution: string; subscriberUID: string } | null,
        };

        const defaultWidgetOptions = {
            symbol: currentTickerString,
            datafeed: newDatafeed as any,
            interval: 'D',
            container_id: chartContainerRef.current.id,
            locale: locale,
            timezone: timezone,
            loading_screen: { backgroundColor: "#181a21" },
            disabled_features: ['header_symbol_search', 'use_localstorage_for_settings'],
            enabled_features: ['hide_left_toolbar_by_default'],
            overrides: {
                "mainSeriesProperties.candleStyle.upColor": "#26a69a",
                "mainSeriesProperties.candleStyle.downColor": "#ef5350",
                "mainSeriesProperties.candleStyle.borderUpColor": "#26a69a",
                "mainSeriesProperties.candleStyle.borderDownColor": "#ef5350",
                "mainSeriesProperties.candleStyle.wickUpColor": "#26a69a",
                "mainSeriesProperties.candleStyle.wickDownColor": "#ef5350"
            }
        };

        const tvWidget = new widget({ ...defaultWidgetOptions, ...widgetOptions('dark') });
        tvWidgetRef.current = tvWidget;

        // Store the datafeed reference for real-time updates
        setDatafeed(newDatafeed);
        datafeedRef.current = newDatafeed;

        if (tvWidget) {
            tvWidget.onChartReady(() => {
                console.log('Chart is ready, datafeed state:', {
                    hasDatafeed: !!datafeed,
                    hasRealtimeCallback: !!datafeed?.onRealtimeCallback,
                    hasResetCacheCallback: !!datafeed?.onResetCacheNeededCallback,
                    currentSubscription: subscriptionRef.current,
                    lastSubscriberUID: lastSubscriberUIDRef.current
                });

                // Function to hide legend elements - only for ChartScreen
                const hideLegendElements = () => {
                    // Only apply this logic if we're on the ChartScreen
                    if (!window.location.pathname.includes('/chart')) {
                        return;
                    }

                    try {
                        // Get the iframe content
                        const iframe = document.querySelector('#tradingview_chart_container iframe') as HTMLIFrameElement;
                        if (iframe && iframe.contentDocument) {
                            const iframeDoc = iframe.contentDocument;
                            
                            // Hide specific legend elements
                            const selectors = [
                                '.pane-legend-line',
                                '.pane-legend-wrap',
                                '.pane-legend',
                                '.pane-legend-title__container',
                                '.apply-overflow-tooltip',
                                '.apply-overflow-tooltip--allow-text',
                                '.apply-overflow-tooltip--check-children',
                                '.apply-overflow-tooltip.pane-legend-title__container',
                                '[class*="legend"]',
                                '[class*="pane-legend"]',
                                '[id*="legend"]',
                                '[data-name*="legend"]'
                            ];

                            selectors.forEach(selector => {
                                const elements = iframeDoc.querySelectorAll(selector);
                                elements.forEach(element => {
                                    if (element) {
                                        (element as HTMLElement).style.display = 'none';
                                        (element as HTMLElement).style.visibility = 'hidden';
                                        (element as HTMLElement).style.opacity = '0';
                                        (element as HTMLElement).style.height = '0';
                                        (element as HTMLElement).style.overflow = 'hidden';
                                    }
                                });
                            });
                        }
                    } catch (error) {
                        console.log('Cannot access iframe content (cross-origin):', error);
                    }
                };

                // Hide legend elements immediately
                hideLegendElements();

                // Hide legend elements after a delay to catch dynamically loaded elements
                setTimeout(hideLegendElements, 1000);
                setTimeout(hideLegendElements, 3000);
                setTimeout(hideLegendElements, 5000);

                // Use MutationObserver to watch for dynamically added elements
                const observer = new MutationObserver(() => {
                    hideLegendElements();
                });

                // Start observing
                if (document.body) {
                    observer.observe(document.body, {
                        childList: true,
                        subtree: true
                    });
                }

                // Reset chart data and send initial data when chart is ready
                if (data.length > 0) {
                    newDatafeed.onResetCacheNeededCallback?.();
                    data.forEach(bar => {
                        newDatafeed.onRealtimeCallback?.({
                            time: new Date(bar.time).getTime(),
                            open: bar.open,
                            high: bar.high,
                            low: bar.low,
                            close: bar.close,
                            volume: bar.volume,
                        });
                    });
                }

                // Clean up observer on unmount
                return () => {
                    observer.disconnect();
                };
            });
        }

        return () => {
            if (tvWidgetRef.current) {
                tvWidgetRef.current.remove();
                tvWidgetRef.current = null;
            }
            setDatafeed(null);
            datafeedRef.current = null;
            subscriptionRef.current = null;
            lastSubscriberUIDRef.current = null;
        };
    }, [data, currentTickerString, colorTheme, locale, timezone]);

    // Add effect to handle real-time updates
    useEffect(() => {
        
        console.log('newItem received:', newItem);
        console.log('Current datafeed state:', {
            hasDatafeed: !!datafeed,
            hasRealtimeCallback: !!datafeed?.onRealtimeCallback,
            hasResetCacheCallback: !!datafeed?.onResetCacheNeededCallback,
            currentSubscription: subscriptionRef.current,
            lastSubscriberUID: lastSubscriberUIDRef.current
        });

        if (!newItem) return;

        const timer = setTimeout(() => {
            // Try to use ref first, then fallback to state
            const currentDatafeed = datafeedRef.current || datafeed;
            
            if (!currentDatafeed?.onRealtimeCallback) {
                console.warn('No realtime callback available', {
                    hasDatafeed: !!currentDatafeed,
                    currentSubscription: subscriptionRef.current,
                    lastSubscriberUID: lastSubscriberUIDRef.current
                });
                return;
            }

            const newBar = {
                time: new Date(newItem.time).getTime(),
                open: newItem.open,
                high: newItem.high,
                low: newItem.low,
                close: newItem.close,
                volume: newItem.volume,
            };

            try {
                currentDatafeed.onRealtimeCallback(newBar);
                currentDatafeed.onResetCacheNeededCallback?.();
                console.log('Real-time update sent successfully');
            } catch (error) {
                console.error('Error sending real-time update:', error);
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [newItem, datafeed]);

    useEffect(() => {
        if (realTimeData) {
            const currentData = transformDataToChartFormat(
                realTimeData.find((item: any) => item["('listing', 'symbol')"] === currentTickerString)
            );

            if (currentData) {
                // Ensure time is in milliseconds
                const timeMs = new Date(currentData.time).getTime();
                setNewItem({
                    ...currentData,
                    time: timeMs
                });
            }
        }
    }, [realTimeData, currentTickerString, dispatch]);

    // Handle ticker changes
    useEffect(() => {
        const currentDatafeed = datafeedRef.current || datafeed;
        if (currentDatafeed?.onRealtimeCallback) {
            currentDatafeed.onResetCacheNeededCallback?.();
        }
    }, [currentTickerString, datafeed]);

    const transformDataToChartFormat = (data: any) => {
        if (!data) return null;
        return {
            time: data["('listing', 'trading_date')"] + 'T' + data["('match', 'sending_time')"].split(' ')[1].split('.')[0],
            open: data["('match', 'reference_price')"] / 1000,
            high: data["('match', 'highest')"] / 1000,
            low: data["('match', 'lowest')"] / 1000,
            close: data["('match', 'match_price')"] / 1000,
            volume: data["('match', 'match_vol')"] / 1000
        };
    };

    useEffect(() => {
        if (!currentGroup) {
            dispatch(fetchTickersGroupRequest(DEFAULT_GROUP));
            return;
        }

        const socket = new WebSocket(appUrls.socketDataURL);

        socket.onopen = () => {
            socket.send(JSON.stringify({ type: 'subscribe', group: currentGroup }));
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event?.data);
                setRealTimeData(data);
            } catch (error) {
                console.error('Error processing WebSocket data:', error);
            }
        };

        socket.onerror = (error) => {
            console.error('WebSocket Error:', error);
            console.log('WebSocket readyState on error:', socket.readyState);
        };

        socket.onclose = (event) => {
            console.log('WebSocket closed:', {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                readyState: socket.readyState
            });
        };

        // Add ping/pong to keep connection alive
        const pingInterval = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
                console.log('Sending ping');
                socket.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000); // Send ping every 30 seconds

        return () => {
            console.log('Cleaning up WebSocket connection');
            clearInterval(pingInterval);
            if (socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
        };
    }, [currentGroup, dispatch]);

    return (
        <div
            id="tradingview_chart_container"
            ref={chartContainerRef}
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
            }}
        />
    );
};

export default StockChart;
